import { InfoIcon } from "@phosphor-icons/react/dist/csr/Info";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@volca/ui/components/ui/alert";
import { Button } from "@volca/ui/components/ui/button";
import { Stack } from "@volca/ui/components/ui/stack";
import {
  type TimeRange,
  TimeRangePicker,
} from "@volca/ui/components/ui/time-range-picker";
import { toast } from "@volca/ui/lib/toast";
import { REPORT_PERIOD_PRESETS } from "@/profitability/constants";
import { GoalChecks } from "@/profitability/goal-checks";
import { periodDates } from "@/profitability/new-run/run-period";
import { usePublishReport, useReports } from "@/profitability/report-query";
import { profitabilityTemplatePath } from "@/profitability/routes";
import type { ProfitabilityRun } from "@/profitability/types";
import { formatReportPeriod, reportError } from "@/profitability/utils";

export const DraftSampleBanner = ({
  businessId,
  run,
}: {
  businessId: string;
  run: Pick<ProfitabilityRun, "id" | "report" | "workflow_version">;
}) => {
  const navigate = useNavigate();
  const reports = useReports(businessId, run.workflow_version);
  const publish = usePublishReport(businessId);
  // The period the owner says the sample covers, when its files don't.
  const [said, setSaid] = useState<TimeRange | null>(null);
  const template = reports.data?.find((report) => report.id === run.report?.id);
  // Until the agent loads (or if it can't), which version this sampled is
  // unknown, and the banner says only what is true of every sample.
  const pending =
    !!template?.draft_pending && template.draft_sample_run_id === run.id;
  const firstBuild = !!template && !template.published;
  const inferred = template?.draft_sample_period ?? null;

  return (
    <Alert
      color="information"
      className="mb-4"
      data-testid="draft-sample-banner"
    >
      <InfoIcon className="size-5" />
      <AlertTitle>
        {!template
          ? "Sample run"
          : !pending
            ? "Sample run of an earlier version"
            : firstBuild
              ? "Sample run of the new agent"
              : "Sample run of the draft changes"}
      </AlertTitle>
      <AlertDescription>
        <Stack align="start" gap={2}>
          <span>
            {!template
              ? "The agent, run on the files it was built from. It is not one of the agent's periods."
              : !pending
                ? "This version of the agent, run on the files it was built from. It is not one of the agent's periods."
                : firstBuild
                  ? "This ran the agent on the documents it was built from. It waits for you: publish it to use it for new runs."
                  : "This ran the new version on the documents it was built from. New runs keep using the published version until you publish this one."}
          </span>
          {pending && firstBuild && (
            <span>
              {inferred
                ? `Publishing runs these files for ${formatReportPeriod(inferred)}, the period their dates fall in. The reviewed sample stays unchanged.`
                : "Its files don't fall in one period. Say which period to run it for, or publish without one to leave it out of the history."}
            </span>
          )}
          {pending && firstBuild && !inferred && (
            <TimeRangePicker
              placeholder="No period"
              presets={REPORT_PERIOD_PRESETS}
              value={said}
              onChange={setSaid}
              allowTextEntry
            />
          )}
          {pending && template?.checks?.draft && (
            <GoalChecks checks={template.checks.draft} />
          )}
          {pending && template && template.draft_version_id && (
            <Stack align="start" direction="row" gap={1}>
              <Button
                disabled={publish.isPending}
                aria-busy={publish.isPending}
                onClick={() =>
                  publish.mutate(
                    {
                      reportId: template.id,
                      versionId: template.draft_version_id!,
                      period: inferred ? null : periodDates(said),
                    },
                    {
                      onSuccess: () => {
                        toast.success(`${template.name} is ready to run`);
                        navigate(profitabilityTemplatePath(template.id));
                      },
                      onError: (error) =>
                        toast.error("The agent could not be published", {
                          description: reportError(error),
                        }),
                    },
                  )
                }
              >
                Publish agent
              </Button>
            </Stack>
          )}
        </Stack>
      </AlertDescription>
    </Alert>
  );
};
